import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, MessageCircle, Coins, UserPlus, Reply } from "lucide-react";
import { api, listNotifications, unreadCount, markNotifRead, markAllRead, wsUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { NOTIF } from "@/constants/testIds";

const ICONS = {
  comment: <MessageCircle className="w-4 h-4 text-[#00F0FF]" />,
  reply: <Reply className="w-4 h-4 text-[#00F0FF]" />,
  tip: <Coins className="w-4 h-4 text-[#FFE300]" />,
  tip_comment: <Coins className="w-4 h-4 text-[#FFE300]" />,
  follow: <UserPlus className="w-4 h-4 text-[#39FF14]" />,
};

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const d = await listNotifications(30);
      setItems(d.notifications || []);
      setUnread(d.unread || 0);
    } catch {}
  }, [user]);

  // Realtime WebSocket connection with auto-reconnect + polling fallback
  useEffect(() => {
    if (!user) return;
    let ws = null;
    let closed = false;
    let reconnectTimer = null;
    let pingTimer = null;
    let pollTimer = null;

    const startPollingFallback = () => {
      const tick = async () => {
        try {
          const d = await unreadCount();
          setUnread(d.unread);
        } catch {}
        pollTimer = setTimeout(tick, 30000);
      };
      tick();
    };

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl());
        ws.onopen = () => {
          if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
          pingTimer = setInterval(() => { try { ws.send("ping"); } catch {} }, 25000);
        };
        ws.onmessage = (evt) => {
          try {
            const d = JSON.parse(evt.data);
            if (d.type === "hello") setUnread(d.unread || 0);
            if (d.type === "notification") {
              setUnread((u) => u + 1);
              setItems((prev) => [d.notification, ...prev].slice(0, 30));
            }
          } catch {}
        };
        ws.onclose = () => {
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          if (closed) return;
          // fallback to polling and try to reconnect in 8s
          if (!pollTimer) startPollingFallback();
          reconnectTimer = setTimeout(connect, 8000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        if (!pollTimer) startPollingFallback();
      }
    };
    connect();

    // initial unread
    unreadCount().then((d) => setUnread(d.unread)).catch(() => {});

    return () => {
      closed = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pollTimer) clearTimeout(pollTimer);
      if (ws) try { ws.close(); } catch {}
    };
  }, [user]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) refresh();
  };

  const handleReadAll = async () => {
    await markAllRead();
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClick = async (n) => {
    if (!n.read) {
      await markNotifRead(n.id);
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
  };

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button data-testid={NOTIF.bell} onClick={toggle}
        className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#00F0FF]/50 hover:text-[#00F0FF] relative transition-colors">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF003C] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0A0A0E]">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid={NOTIF.dropdown} className="absolute right-0 top-11 w-80 sm:w-96 max-h-[70vh] overflow-hidden glass-strong rounded-2xl border border-white/10 flex flex-col z-50">
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF]">// notifications</div>
              <div className="font-heading font-bold">{unread ? `${unread} unread` : "You're all caught up"}</div>
            </div>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button data-testid={NOTIF.readAll} onClick={handleReadAll}
                  className="mono text-[10px] uppercase tracking-widest text-white/70 hover:text-[#00F0FF] flex items-center gap-1">
                  <Check className="w-3 h-3" /> Mark all
                </button>
              )}
              {items.length > 0 && (
                <button data-testid="notif-clear-all" onClick={async () => { await api.delete("/notifications"); setItems([]); setUnread(0); }}
                  className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#FF003C]">Clear</button>
              )}
              <Link to="/settings/notifications" onClick={() => setOpen(false)} data-testid="notif-settings-link" className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#00F0FF]">Settings</Link>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 && (
              <div data-testid={NOTIF.empty} className="py-10 text-center px-6">
                <div className="mono text-xs text-white/40 uppercase tracking-widest mb-1">// nothing yet</div>
                <p className="text-white/60 text-sm">Tips, comments and follows on your apps show up here.</p>
              </div>
            )}
            {items.map((n) => (
              <Link
                key={n.id}
                to={n.link || "/profile"}
                data-testid={NOTIF.item}
                onClick={() => { setOpen(false); handleClick(n); }}
                className={`block px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${n.read ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: `${n.actor?.color || "#00F0FF"}22`, border: `1px solid ${n.actor?.color || "#00F0FF"}55` }}>
                    {ICONS[n.kind] || <Bell className="w-4 h-4 text-white/60" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white/90 leading-snug">{n.title}</div>
                    {n.body && <div className="text-xs text-white/50 mt-0.5 line-clamp-2">"{n.body}"</div>}
                    <div className="mono text-[10px] text-white/40 mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read && (
                    <span className="w-2 h-2 rounded-full bg-[#00F0FF] mt-2 flex-shrink-0" />
                  )}
                  <button aria-label="Delete notification" data-testid={`notif-delete-${n.id}`}
                    onClick={async (e) => { e.preventDefault(); e.stopPropagation(); await api.delete(`/notifications/${n.id}`); setItems((p) => p.filter((x) => x.id !== n.id)); if (!n.read) setUnread((u) => Math.max(0, u - 1)); }}
                    className="w-7 h-7 -mr-1 rounded-full flex items-center justify-center text-white/60 hover:text-[#FF003C] hover:bg-white/10 text-lg leading-none">×</button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
